import type { JSX, ReactNode } from 'react'

/**
 * FormField — the canonical label + control + error/help wrapper.
 * Standardises label typography, the required-asterisk, error styling and
 * helper text so every field across the app lines up and behaves the same.
 *
 * It is control-agnostic: drop any input (PrimeReact, native, custom) as the
 * child. Pass `htmlFor` matching the control's `id` for proper label linkage.
 */
export function FormField({
  label,
  htmlFor,
  required = false,
  error,
  help,
  hint,
  children,
  className = '',
}: {
  label?: string | undefined
  htmlFor?: string | undefined
  required?: boolean | undefined
  error?: string | undefined
  /** Muted helper text shown below the control (hidden when an error shows). */
  help?: string | undefined
  /** Optional right-aligned node next to the label (e.g. a "Generate" link). */
  hint?: ReactNode | undefined
  children: ReactNode
  className?: string | undefined
}): JSX.Element {
  return (
    <div className={`flex flex-col ${className}`}>
      {(label || hint) && (
        <div className="mb-1.5 flex items-center justify-between gap-2">
          {label && (
            <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700">
              {label}
              {required && <span className="ml-1 text-red-500">*</span>}
            </label>
          )}
          {hint && <span className="text-xs text-gray-400">{hint}</span>}
        </div>
      )}
      {children}
      {error ? (
        <p role="alert" className="mt-1 text-xs font-medium text-red-500">
          {error}
        </p>
      ) : (
        help && <p className="mt-1 text-xs text-gray-500">{help}</p>
      )}
    </div>
  )
}

/**
 * FieldGroup — a labelled sub-group of related fields inside a section, set on
 * a subtle surface so the grouping reads clearly without adding another card.
 */
export function FieldGroup({
  title,
  description,
  children,
  className = '',
}: {
  title?: string | undefined
  description?: string | undefined
  children: ReactNode
  className?: string | undefined
}): JSX.Element {
  return (
    <div className={`rounded-lg border border-gray-200 bg-gray-50/60 p-5 shadow-sm md:p-6 ${className}`}>
      {(title || description) && (
        <div className="mb-4">
          {title && <h3 className="text-sm font-semibold text-gray-900">{title}</h3>}
          {description && <p className="mt-1 text-xs text-gray-500">{description}</p>}
        </div>
      )}
      {children}
    </div>
  )
}
